import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { motion } from "framer-motion";
import type { Notification } from "../../types/notifications";
import {
    AlertTriangle,
    MoreVertical,
    Check,
    Trash2,
    Bell,
    Info,
    Home,
} from "lucide-react";
import { getPlatformIcon } from "../../utils/logo-getter";

interface NotificationItemProps {
    notification: Notification;
    onRead: () => void;
    onRemove: () => void;
    setOpen?: (open: boolean) => void;
}

const NotificationOverlayItem = ({
    notification,
    onRead,
    onRemove,
    setOpen,
}: NotificationItemProps) => {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    
    useEffect(() => {
        const onClickOutside = (e: MouseEvent) => {
            if (
                menuOpen &&
                menuRef.current &&
                !menuRef.current.contains(e.target as Node)
            ) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, [menuOpen]);

    const handleClick = () => {
        if (!notification.isRead) onRead();
        setOpen && setOpen(false);
        setMenuOpen(false);
        navigate("/notifications", {
            state: { notificationId: notification._id },
        });
    };

    const toggleMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        setMenuOpen(!menuOpen);
    };

    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation();
        onRemove();
        setMenuOpen(false);
    };

    const handleMarkRead = (e: React.MouseEvent) => {
        e.stopPropagation();
        onRead();
        setMenuOpen(false);
    };

    const getNotificationIcon = () => {
        switch (notification.type) {
            case "announcement":
                return <Bell className="w-5 h-5 text-[#E9C468]" />;
            case "alert":
                return <AlertTriangle className="w-5 h-5 text-[#ffbe00]" />;
            case "listingIngested":
                return <Home className="w-5 h-5 text-[#193456]" />;
            case "captionVideoReady": {
                const platformIcon = getPlatformIcon(
                    notification.payload?.platform ?? ""
                );
                return platformIcon ? (
                    <span className="w-5 h-5 flex items-center justify-center [&>svg]:w-5 [&>svg]:h-5">
                        {platformIcon}
                    </span>
                ) : (
                    <Bell className="w-5 h-5 text-[#E9C468]" />
                );
            }
            default:
                return <Info className="w-5 h-5 text-[#193456]" />;
        }
    };

    const handleViewClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!notification.isRead) onRead();
        setOpen?.(false);
        setMenuOpen(false);
        const url = notification.payload?.postId
            ? `/post/${notification.payload.postId}`
            : notification.url;
        if (url) navigate(url);
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, height: 0 }}
            className={`relative group p-4 hover:bg-gray-50 cursor-pointer transition-colors duration-200 ${
                !notification.isRead ? "bg-[#F0DFAF]/20" : ""
            }`}
            ref={menuRef}
            onClick={handleClick}
        >
            <div className="flex items-start gap-3">
                <div className="flex-shrink-0 p-1.5 bg-white rounded-full shadow-sm border border-gray-100">
                    {getNotificationIcon()}
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <h4
                            className={`text-sm font-medium ${
                                !notification.isRead
                                    ? "text-[#193456]"
                                    : "text-gray-700"
                            }`}
                        >
                            {notification.title}
                        </h4>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                            {formatDistanceToNow(
                                new Date(notification.createdAt),
                                { addSuffix: true }
                            )}
                        </span>
                        {notification.type === "captionVideoReady" &&
                            (notification.payload?.postId || notification.url) && (
                                <>
                                    <span
                                        aria-hidden
                                        className="text-xs text-gray-400"
                                    >
                                        ·
                                    </span>
                                    <button
                                        type="button"
                                        onClick={handleViewClick}
                                        className="text-xs font-medium text-[#193456] hover:text-[#E9C468] hover:underline focus:outline-none focus:underline"
                                    >
                                        View
                                    </button>
                                </>
                            )}
                    </div>
                </div>

                <div className="relative flex-shrink-0">
                    <button
                        onClick={toggleMenu}
                        className="p-1 text-gray-400 hover:text-gray-600 rounded-full opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    >
                        <MoreVertical className="w-4 h-4" />
                    </button>

                    {menuOpen && (
                        <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg border border-gray-100 z-10">
                            {!notification.isRead && (
                                <button
                                    onClick={handleMarkRead}
                                    className="flex items-center w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-[#F0DFAF]"
                                >
                                    <Check className="w-4 h-4 mr-2 text-[#193456]" />
                                    Mark as read
                                </button>
                            )}
                            <button
                                onClick={handleRemove}
                                className="flex items-center w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-[#F0DFAF]"
                            >
                                <Trash2 className="w-4 h-4 mr-2 text-red-500" />
                                Remove
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {!notification.isRead && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-[#E9C468] rounded-r-full"></div>
            )}
        </motion.div>
    );
};

export default NotificationOverlayItem;
